import { apiClient } from '@/api/http/client'
import type { ApiResponse } from '@/api/http/types'
import { buildTenantHostname } from '@/features/auth/lib/tenant-host'
import {
  setTenantHandoffFailedFlag,
} from '@/features/auth/lib/constants'

export interface SubdomainHandoffOptions {
  readonly postInviteWelcome?: boolean
}

/** sessionStorage: timestamp when a tenant handoff navigation was started (apex origin). */
export const TENANT_HANDOFF_STARTED_AT = 'bf_tenant_handoff_started_at' as const

/**
 * Last handoff attempt time on this origin. Survives soft-bounce (which only
 * clears {@link TENANT_HANDOFF_STARTED_AT}) so the bounce window still applies.
 */
export const TENANT_HANDOFF_LAST_ATTEMPT_AT =
  'bf_tenant_handoff_last_attempt_at' as const

/**
 * How many handoff navigations were started from this origin in the current
 * bounce window. Soft-bounce after the first attempt must be allowed to retry;
 * only a second failed return within the window is treated as a loop.
 */
export const TENANT_HANDOFF_ATTEMPT_COUNT = 'bf_tenant_handoff_attempt_count' as const

/** If we return to the apex within this window after enough attempts, stop retrying. */
export const TENANT_HANDOFF_LOOP_WINDOW_MS = 15_000

/** Abort further handoffs after this many attempts in the bounce window. */
export const TENANT_HANDOFF_MAX_ATTEMPTS = 2

let handoffFlight: Promise<boolean> | null = null

function readStoredTime(key: string): number | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(key)
    if (!raw) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}

export function readTenantHandoffStartedAt(): number | null {
  return readStoredTime(TENANT_HANDOFF_STARTED_AT)
}

export function readTenantHandoffLastAttemptAt(): number | null {
  return readStoredTime(TENANT_HANDOFF_LAST_ATTEMPT_AT)
}

export function readTenantHandoffAttemptCount(): number {
  try {
    const raw = globalThis.sessionStorage?.getItem(TENANT_HANDOFF_ATTEMPT_COUNT)
    if (!raw) return 0
    const n = Number(raw)
    return Number.isFinite(n) && n > 0 ? n : 0
  } catch {
    return 0
  }
}

export function markTenantHandoffStarted(): void {
  try {
    const next = readTenantHandoffAttemptCount() + 1
    const now = String(Date.now())
    globalThis.sessionStorage?.setItem(TENANT_HANDOFF_ATTEMPT_COUNT, String(next))
    globalThis.sessionStorage?.setItem(TENANT_HANDOFF_LAST_ATTEMPT_AT, now)
    globalThis.sessionStorage?.setItem(TENANT_HANDOFF_STARTED_AT, now)
  } catch {
    // ignore
  }
}

export function clearTenantHandoffStarted(): void {
  try {
    globalThis.sessionStorage?.removeItem(TENANT_HANDOFF_STARTED_AT)
    globalThis.sessionStorage?.removeItem(TENANT_HANDOFF_LAST_ATTEMPT_AT)
    globalThis.sessionStorage?.removeItem(TENANT_HANDOFF_ATTEMPT_COUNT)
  } catch {
    // ignore
  }
}

/**
 * Soft-bounce helper: drop only the in-flight timestamp so apex can schedule
 * another handoff, while preserving attempt count + last-attempt time for
 * loop detection.
 */
export function clearTenantHandoffStartedTimestamp(): void {
  try {
    globalThis.sessionStorage?.removeItem(TENANT_HANDOFF_STARTED_AT)
  } catch {
    // ignore
  }
}

/**
 * True when handoff already failed enough times in the bounce window.
 *
 * Important: a *single* soft-bounce back to apex (cookies still settling after
 * `/handoff/use`, or account-switch races) must NOT abort — that was stranding
 * Sensemaker users on `localhost` after Platform Admin ↔ tenant switches.
 */
export function isTenantHandoffBounceLoop(): boolean {
  if (readTenantHandoffAttemptCount() < TENANT_HANDOFF_MAX_ATTEMPTS) {
    return false
  }
  const lastAttempt = readTenantHandoffLastAttemptAt()
  if (lastAttempt === null) return false
  return Date.now() - lastAttempt < TENANT_HANDOFF_LOOP_WINDOW_MS
}

/**
 * Create a handoff code and navigate to `{subdomain}.{apex}/` (never `/login`).
 * Single-flight so LoginPage and TenantHostRedirect cannot double-assign.
 */
export async function redirectToSubdomainHandoff(
  subdomain: string,
  options?: SubdomainHandoffOptions,
): Promise<boolean> {
  if (handoffFlight) return handoffFlight

  handoffFlight = (async () => {
    try {
      const ts = subdomain.trim()
      if (!ts) return false

      const { protocol, hostname, port } = globalThis.location
      const targetHost = buildTenantHostname(hostname, ts)
      if (targetHost === hostname) {
        clearTenantHandoffStarted()
        return false
      }

      const res = await apiClient.post<
        ApiResponse<{ code: string; expires_in: number }>
      >('/api/v1/auth/handoff/create', undefined, {
        skipUnauthorizedRedirect: true,
      })
      const code = res.data.data?.code
      if (!code) {
        setTenantHandoffFailedFlag()
        return false
      }

      const portSuffix = port ? `:${port}` : ''
      // Always land on `/` so RequireGuest cannot bounce tenant `/login` → apex.
      const handoffUrl = new URL(`${protocol}//${targetHost}${portSuffix}/`)
      handoffUrl.searchParams.set('_handoff', code)
      if (options?.postInviteWelcome) {
        handoffUrl.searchParams.set('postInviteWelcome', '1')
      }
      markTenantHandoffStarted()
      globalThis.location.assign(handoffUrl.toString())
      return true
    } catch {
      setTenantHandoffFailedFlag()
      return false
    } finally {
      handoffFlight = null
    }
  })()

  return handoffFlight
}
